import { useCallback, useEffect, useState } from 'react';
import { addWeeks } from 'date-fns/addWeeks';
import { format } from 'date-fns/format';
import { subWeeks } from 'date-fns/subWeeks';
import { startOfWeek } from 'date-fns-jalali/startOfWeek';
import { useSelfContext } from '@/context';

const WEEK_COUNT = 10;
const CURRENT_WEEK_INDEX = 5;

function toWeekStartParam(date: Date) {
  return `${format(date, 'yyyy-MM-dd')}+00:00:00`;
}

export default function useWeekChangeManagement(
  onSelectDay?: (day: string) => void
) {
  const { changeWeek } = useSelfContext();
  const [currentDot, setCurrentDot] = useState<number>(CURRENT_WEEK_INDEX);
  const [weekDates, setWeekDates] = useState<string[]>([]);

  const selectWeek = useCallback((index: number) => {
    const selectedDate = weekDates[index];
    if (!selectedDate) return index;
    changeWeek(selectedDate);
    onSelectDay?.(selectedDate);
    return index;
  }, [changeWeek, onSelectDay, weekDates]);

  const onPressLeft = useCallback(() => {
    setCurrentDot((current) => selectWeek(Math.min(current + 1, weekDates.length - 1)));
  }, [selectWeek, weekDates.length]);

  const onPressRight = useCallback(() => {
    setCurrentDot((current) => selectWeek(Math.max(current - 1, 0)));
  }, [selectWeek]);

  const onPressDot = useCallback((index: number) => {
    setCurrentDot(selectWeek(index));
  }, [selectWeek]);

  useEffect(() => {
    const currentWeekStart = startOfWeek(new Date());
    const firstWeekStart = subWeeks(currentWeekStart, CURRENT_WEEK_INDEX);
    const dates = Array.from({ length: WEEK_COUNT }, (_, index) => (
      toWeekStartParam(addWeeks(firstWeekStart, index))
    ));

    setWeekDates(dates);
    setCurrentDot(CURRENT_WEEK_INDEX);
  }, []);

  return { currentDot, onPressLeft, onPressRight, onPressDot, weekDates };
}
